import { EventObject } from '@xstate/store-react';
import { NextEventData } from '../../models/store/nextEvent';
import { StoreContext } from '../../models/store/context';
import { log } from '@domgen/shared/src/utils/log';
import { StepKey } from '../../enums/stepKey';
import { StoreEvent } from '../../enums/storeEvent';
import { saveSession } from '../../services/session/save';
import { AppRoute } from '../../enums/appRoutes';
import { getNextStepPath } from '../../utils/router/getNextStepPath';
import { modelMatchesLookupAndCatalogue } from '../../utils/store/modelMatchesLookupAndCatalogue';

/**
 * @description Next Event Handler
 * This event handler is responsible for handling the "next" event in the application.
 * When this event is triggered, it takes the form data from the event, updates the context's form with this new data, and if the current step is the MODEL_SERIAL step, it also checks if a model number is present and if there are appliance lookup results in the context. If so, it tries to find a matched appliance based on the model number and updates the form with the appliance details if a match is found. Finally, it saves the updated session context and navigates the user to the next step based on the current step key.
 * @param {StoreContext} context - The current store context
 * @param {EventObject & { data: NextEventData }} event - The event object containing the form data for the next step in event.data
 * @param {{ effect: (effect: () => Promise<void>) => void; emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void } }} enqueue - An object with an effect function to enqueue side effects and an emit function to navigate to a different route
 * @returns {StoreContext} Updated context after processing the next event and updating form data accordingly
 */
export const next = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data: NextEventData },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  log.debug({ message: StoreEvent.NEXT, data: event.data });
  let applianceFoundInLookup = false;
  const stepKey = Object.keys(event.data)[0] as StepKey;
  const form = {
    ...context.form,
    ...event.data,
  };

  /**
   * [IMPORTANT] This block of code is responsible for handling the specific logic related to the MODEL_SERIAL step in the form.
   * When the user submits the MODEL_SERIAL step, it checks if a model number is provided and if there are appliance lookup results available in the context.
   * If both conditions are met, it attempts to find a matched appliance based on the model number. If a matched appliance is found and the catalogue data is available in the context, it retrieves the category for that appliance from the catalogue and updates the form with the appliance details (super category and item category).
   *
   * This logic is crucial for ensuring that when a user enters their model serial information, the application can automatically populate relevant appliance details based on existing data, enhancing the user experience and streamlining the registration process.
   */
  if (stepKey === StepKey.MODEL_SERIAL) {
    applianceFoundInLookup = modelMatchesLookupAndCatalogue({ ...context, form });
    if (!applianceFoundInLookup && context.journeyType === 'test') {
      delete form[StepKey.APPLIANCE_DETAILS];
    }
  }

  const updatedContext = {
    ...context,
    form,
    quote: undefined,
  };

  if (context.journeyType === 'test' && applianceFoundInLookup) {
    enqueue.emit.navigate({ path: AppRoute.PURCHASE_PRICE });
  } else {
    const nextPath = getNextStepPath(stepKey, context.journeyType);
    if (nextPath) {
      enqueue.emit.navigate({ path: nextPath });
    }
  }
  saveSession(updatedContext);
  return updatedContext;
};
